import { useRef } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Bold, Italic, List, Link2 } from 'lucide-react';

export default function ForumEditor({ value, onChange, rows = 6, placeholder = "Write your post...", testId = "forum-editor" }) {
  const ref = useRef(null);

  const wrapSelection = (before, after) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.substring(start, end);
    const replacement = selected
      ? `${before}${selected}${after}`
      : `${before}text${after}`;
    const newVal = value.substring(0, start) + replacement + value.substring(end);
    onChange(newVal);
    // Restore cursor position after React re-render
    setTimeout(() => {
      el.focus();
      const cursorPos = selected ? start + replacement.length : start + before.length;
      el.setSelectionRange(cursorPos, selected ? cursorPos : cursorPos + 4);
    }, 0);
  };

  const insertBold = () => wrapSelection('**', '**');
  const insertItalic = () => wrapSelection('*', '*');

  const insertBullet = () => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    // Find the beginning of the current line
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const newVal = value.substring(0, lineStart) + '- ' + value.substring(lineStart);
    onChange(newVal);
    setTimeout(() => { el.focus(); el.setSelectionRange(lineStart + 2, lineStart + 2); }, 0);
  };

  const insertLink = () => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.substring(start, end);
    const url = selected.startsWith('http') ? selected : 'https://';
    const linkText = selected.startsWith('http') ? 'link text' : (selected || 'link text');
    const replacement = `[${linkText}](${url})`;
    const newVal = value.substring(0, start) + replacement + value.substring(end);
    onChange(newVal);
    setTimeout(() => { el.focus(); }, 0);
  };

  return (
    <div data-testid={testId}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 mb-1.5 p-1.5 bg-slate-50 rounded-t-xl border border-b-0 border-slate-200">
        <Button type="button" variant="ghost" size="sm" onClick={insertBold} className="h-7 w-7 p-0 hover:bg-slate-200 rounded" title="Bold" data-testid="editor-bold-btn">
          <Bold className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={insertItalic} className="h-7 w-7 p-0 hover:bg-slate-200 rounded" title="Italic" data-testid="editor-italic-btn">
          <Italic className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={insertBullet} className="h-7 w-7 p-0 hover:bg-slate-200 rounded" title="Bullet list" data-testid="editor-bullet-btn">
          <List className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={insertLink} className="h-7 w-7 p-0 hover:bg-slate-200 rounded" title="Insert link" data-testid="editor-link-btn">
          <Link2 className="w-3.5 h-3.5" />
        </Button>
        <span className="ml-auto text-[10px] text-slate-400">**bold** &nbsp; *italic* &nbsp; - bullet &nbsp; [text](url) &nbsp; ![alt](img)</span>
      </div>
      <Textarea
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="rounded-t-none rounded-b-xl border-slate-200"
        data-testid={`${testId}-textarea`}
      />
    </div>
  );
}
